import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  min: number;
  max: number;
  sublevels: number;
}

const LEVELS = levelsData.levels as Level[];
const MAX_LIVES = 3;

function fallDurationFor(levelNumber: number): number {
  if (levelNumber <= 2) return 6;
  if (levelNumber <= 5) return 5;
  return 4;
}

const DOT_PATTERNS: Record<number, [number, number][]> = {
  0: [],
  1: [[1, 1]],
  2: [[0, 0], [2, 2]],
  3: [[0, 0], [1, 1], [2, 2]],
  4: [[0, 0], [0, 2], [2, 0], [2, 2]],
  5: [[0, 0], [0, 2], [1, 1], [2, 0], [2, 2]],
  6: [[0, 0], [0, 2], [1, 0], [1, 2], [2, 0], [2, 2]],
  7: [[0, 0], [0, 2], [1, 0], [1, 1], [1, 2], [2, 0], [2, 2]],
  8: [[0, 0], [0, 1], [0, 2], [1, 0], [1, 2], [2, 0], [2, 1], [2, 2]],
  9: [[0, 0], [0, 1], [0, 2], [1, 0], [1, 1], [1, 2], [2, 0], [2, 1], [2, 2]],
};

function buildDotsGrid(value: number): string {
  const positions = DOT_PATTERNS[Math.min(value, 9)] ?? [];
  const cells: string[] = [];
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      const has = positions.some((p) => p[0] === r && p[1] === c);
      cells.push(has ? '<div class="smnum__dot"></div>' : '<div></div>');
    }
  }
  return cells.join('');
}

export function mountSmallNumbers(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0]!;
  const fallSec = fallDurationFor(level.level);
  const totalSubs = level.sublevels;

  let lives = MAX_LIVES;
  let correctCount = 0;
  let currentValue = 0;
  let inputBlocked = true;
  let transitionEndHandler: ((e: TransitionEvent) => void) | null = null;
  let dieEl: HTMLElement | null = null;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Conte o dado</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage smnum">
      <div class="feedback" aria-live="polite"></div>
      <div class="smnum__header">
        <p class="smnum__progress" aria-live="polite"></p>
        <span class="smnum__lives"></span>
      </div>
      <p class="smnum__hint">${level.objective}</p>
      <div class="smnum__playfield">
        <div class="smnum__ground"></div>
      </div>
      <div class="smnum__keypad"></div>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.smnum__progress')!;
  const livesEl = page.querySelector<HTMLElement>('.smnum__lives')!;
  const playfield = page.querySelector<HTMLElement>('.smnum__playfield')!;
  const keypad = page.querySelector<HTMLElement>('.smnum__keypad')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  const onKey = (e: KeyboardEvent): void => {
    if (inputBlocked) return;
    if (e.key >= '0' && e.key <= '9') {
      submitGuess(parseInt(e.key, 10));
      e.preventDefault();
    }
  };
  window.addEventListener('keydown', onKey);

  function renderKeypad(): void {
    keypad.innerHTML = '';
    for (let n = level.min; n <= level.max; n++) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'smnum__key';
      btn.textContent = String(n);
      btn.addEventListener('click', () => submitGuess(n));
      keypad.appendChild(btn);
    }
  }

  function renderProgress(): void {
    progressEl.textContent = `Pergunta ${correctCount + 1} de ${totalSubs}`;
    livesEl.textContent = '❤️'.repeat(lives) + '🤍'.repeat(MAX_LIVES - lives);
  }

  function clearDie(): void {
    if (dieEl && transitionEndHandler) dieEl.removeEventListener('transitionend', transitionEndHandler);
    if (dieEl && dieEl.parentElement) dieEl.parentElement.removeChild(dieEl);
    dieEl = null;
    transitionEndHandler = null;
  }

  function spawnDie(): void {
    clearDie();
    currentValue = Math.floor(Math.random() * (level.max - level.min + 1)) + level.min;
    const el = document.createElement('div');
    el.className = 'smnum__domino smnum__domino--single';
    el.style.setProperty('--fall-duration', `${fallSec}s`);
    el.innerHTML = `<div class="smnum__half">${buildDotsGrid(currentValue)}</div>`;
    playfield.appendChild(el);
    dieEl = el;
    const fieldH = playfield.clientHeight;
    const dieH = 64;
    const ground = 14;
    const distance = fieldH - dieH - ground;
    void el.offsetWidth;
    el.classList.add('smnum__domino--falling');
    el.style.transform = `translate(-50%, ${distance}px)`;
    transitionEndHandler = (e: TransitionEvent) => {
      if (e.propertyName !== 'transform') return;
      if (inputBlocked) return;
      onDieGround();
    };
    el.addEventListener('transitionend', transitionEndHandler);
    inputBlocked = false;
  }

  function submitGuess(guess: number): void {
    if (inputBlocked) return;
    inputBlocked = true;
    if (guess === currentValue) {
      ctx.audio.play('correct');
      showFeedback('ok');
      if (dieEl) dieEl.classList.add('smnum__domino--correct');
      correctCount++;
      window.setTimeout(advance, 400);
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
      if (dieEl) dieEl.classList.add('smnum__domino--wrong');
      lives--;
      window.setTimeout(() => {
        if (lives <= 0) onLose();
        else advanceAfterMistake();
      }, 700);
    }
  }

  function onDieGround(): void {
    inputBlocked = true;
    ctx.audio.play('wrong');
    showFeedback('err');
    if (dieEl) dieEl.classList.add('smnum__domino--wrong');
    lives--;
    window.setTimeout(() => {
      if (lives <= 0) onLose();
      else advanceAfterMistake();
    }, 600);
  }

  function advance(): void {
    if (correctCount >= totalSubs) {
      finishLevel();
    } else {
      renderProgress();
      spawnDie();
    }
  }

  function advanceAfterMistake(): void {
    renderProgress();
    spawnDie();
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function onLose(): void {
    inputBlocked = true;
    clearDie();
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Você perdeu as 3 vidas</h2>
      <p class="activity-finish__text">Acertou ${correctCount} de ${totalSubs} dados. Tente de novo!</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        <button class="btn btn--primary" type="button" data-act="retry">Tentar de novo</button>
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    stage.querySelector<HTMLElement>('[data-act="retry"]')!.addEventListener('click', () => {
      window.location.hash = `/smallnumbers/${level.level}?t=${Date.now()}`;
    });
  }

  function finishLevel(): void {
    inputBlocked = true;
    clearDie();
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você acertou ${correctCount} de ${totalSubs} dados.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) next.addEventListener('click', () => { window.location.hash = `/smallnumbers/${level.level + 1}`; });
  }

  renderKeypad();
  renderProgress();
  spawnDie();

  return {
    unmount(): void {
      window.removeEventListener('keydown', onKey);
      clearDie();
      ctx.root.innerHTML = '';
    },
  };
}
